import { Component, HostListener, OnInit } from '@angular/core';
import { CellClickedEvent, ColDef, GridApi, GridReadyEvent, IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import { NgDialogAnimationService } from 'ng-dialog-animation';
import { NgxSpinnerService } from 'ngx-spinner';
import { Cell, Workbook } from 'exceljs';
import * as fs from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  EmployeeCampus,
  EmployeeCity,
  EmployeeState,
  FeedBackQuestionAndOptionApi,
  FeedbackHistoryOption,
  StudentWellBeingReportRow,
  StudentwellbeingService,
} from '../studentwellbeing.service';
import {
  SingleQuestionDialogComponent,
  SingleQuestionDialogData,
} from '../single-question-dialog/single-question-dialog.component';
import {
  ShowQuestionPaperComponent,
  ShowQuestionPaperDialogData,
} from '../show-question-paper/show-question-paper.component';

interface PdfStudentContext {
  admNo: string;
  feedBackDate: string;
  studentName?: string;
  sectionName?: string;
  selectionsByQuestionId: Map<
    number,
    { optionId: number; optionRank: number; feedbackOptionText: string }[]
  >;
}

const RANK_PALETTE: Record<number, { border: string; circle: string; circleBorder: string }> = {
  1: { border: '#babbbc', circle: 'rgba(220, 38, 38, 0.08)', circleBorder: 'rgb(220, 38, 38)' },
  2: { border: '#babbbc', circle: 'rgba(234, 179, 8, 0.14)', circleBorder: 'rgb(234, 179, 8)' },
  3: { border: '#babbbc', circle: 'rgba(249, 115, 22, 0.1);', circleBorder: 'rgb(249, 115, 22)' },
  4: { border: '#babbbc', circle: 'rgba(14, 165, 233, 0.1);', circleBorder: 'rgb(14, 165, 233)' },
  5: { border: '#babbbc', circle: 'rgba(34, 197, 94, 0.1);', circleBorder: 'rgb(34, 197, 94)' },
};

@Component({
  selector: 'app-studentwellbeing-report',
  templateUrl: './studentwellbeing-report.component.html',
  styleUrls: ['./studentwellbeing-report.component.scss'],
})
export class StudentwellbeingReportComponent implements OnInit {
  readonly categoryName = 'STUDENT WELL BEING';

  filterOpen = false;
  states: EmployeeState[] = [];
  cities: EmployeeCity[] = [];
  campuses: EmployeeCampus[] = [];

  selectedStateId: number | null = null;
  selectedCityId: number | null = null;
  selectedCampusId: number | null = null;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  readonly maxReportDate = new Date();
  stateSearch = '';
  citySearch = '';
  campusSearch = '';
  stateDropdownOpen = false;
  cityDropdownOpen = false;
  campusDropdownOpen = false;
  private stateSearchEdited = false;
  private citySearchEdited = false;
  private campusSearchEdited = false;

  readonly pageSize = 50;
  totalRecords = 0;
  totalPages = 0;
  currentPage = 0;
  serverPaginationEnabled = false;

  rowData: Record<string, unknown>[] = [];
  columnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 72,
    sortable: true,
    filter: false,
    resizable: true,
    suppressHeaderMenuButton: true,
    cellStyle: { textAlign: 'center' },
  };

  private gridApi?: GridApi;
  private questionIds: number[] = [];
  private reportRows: StudentWellBeingReportRow[] = [];

  overlayNoRowsTemplate = '<span>No data found. Apply filters to load report.</span>';
  overlayLoadingTemplate =
    '<span class="ag-overlay-loading-center">Loading report...</span>';

  get canExport(): boolean {
    return this.rowData.length > 0 || this.totalRecords > 0;
  }

  get canApplyFilters(): boolean {
    if (this.selectedStateId !== null) {
      return true;
    }
    return !!(this.fromDate && this.toDate);
  }

  constructor(
    private wellbeingService: StudentwellbeingService,
    private spinner: NgxSpinnerService,
    private dialog: NgDialogAnimationService,
  ) { }

  ngOnInit(): void {
    this.loadStates();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    if (this.isInsideFilterOrDatepicker(target)) {
      if (!target.closest('.report-filter__select-wrap')) {
        this.closeSelectDropdowns();
      }
      return;
    }
    this.closeSelectDropdowns();
    this.filterOpen = false;
  }

  /** Keep filter open while Material datepicker calendar (body overlay) is used. */
  private isInsideFilterOrDatepicker(target: HTMLElement): boolean {
    return !!(
      target.closest('.report-filter') ||
      target.closest('.report-filter-btn') ||
      target.closest('.mat-datepicker-content') ||
      target.closest('.mat-datepicker-popup') ||
      target.closest('.cdk-overlay-pane')
    );
  }

  toggleFilter(event: MouseEvent): void {
    event.stopPropagation();
    this.filterOpen = !this.filterOpen;
  }

  onReportFilterClick(event: MouseEvent): void {
    event.stopPropagation();
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.report-filter__select-wrap')) {
      this.closeSelectDropdowns();
    }
  }

  loadStates(): void {
    this.wellbeingService.getEmployeeStates().subscribe({
      next: (list) => {
        this.states = Array.isArray(list) ? list : [];
      },
      error: () => {
        this.states = [];
      },
    });
  }

  private loadRecentReport(): void {
    this.serverPaginationEnabled = false;
    this.totalRecords = 0;
    this.totalPages = 0;
    this.currentPage = 0;
    this.spinner.show();
    this.gridApi?.showLoadingOverlay();
    this.refreshReportGrid();
  }

  get filteredStates(): EmployeeState[] {
    const search = this.stateDropdownOpen && !this.stateSearchEdited ? '' : this.stateSearch;
    return this.filterListBySearch(this.states, 'stateName', search);
  }

  get filteredCities(): EmployeeCity[] {
    const search = this.cityDropdownOpen && !this.citySearchEdited ? '' : this.citySearch;
    return this.filterListBySearch(this.cities, 'cityName', search);
  }

  get filteredCampuses(): EmployeeCampus[] {
    const search = this.campusDropdownOpen && !this.campusSearchEdited ? '' : this.campusSearch;
    return this.filterListBySearch(this.campuses, 'campusName', search);
  }

  onStateSearchInput(): void {
    this.stateSearchEdited = true;
    this.stateDropdownOpen = true;
    this.cityDropdownOpen = false;
    this.campusDropdownOpen = false;
    const typed = this.stateSearch.trim().toLowerCase();
    const exact = this.states.find((s) => s.stateName.toLowerCase() === typed);
    if (exact) {
      if (this.selectedStateId !== exact.stateId) {
        this.selectedStateId = exact.stateId;
        this.onStateChange();
      }
      return;
    }
    if (this.selectedStateId !== null) {
      this.selectedStateId = null;
      this.onStateChange();
    }
  }

  onCitySearchInput(): void {
    this.citySearchEdited = true;
    this.cityDropdownOpen = true;
    this.stateDropdownOpen = false;
    this.campusDropdownOpen = false;
    const typed = this.citySearch.trim().toLowerCase();
    const exact = this.cities.find((c) => c.cityName.toLowerCase() === typed);
    if (exact) {
      if (this.selectedCityId !== exact.cityId) {
        this.selectedCityId = exact.cityId;
        this.onCityChange();
      }
      return;
    }
    if (this.selectedCityId !== null) {
      this.selectedCityId = null;
      this.onCityChange();
    }
  }

  onCampusSearchInput(): void {
    this.campusSearchEdited = true;
    this.campusDropdownOpen = true;
    this.stateDropdownOpen = false;
    this.cityDropdownOpen = false;
    const typed = this.campusSearch.trim().toLowerCase();
    const exact = this.campuses.find((c) => c.campusName.toLowerCase() === typed);
    this.selectedCampusId = exact?.campusId ?? null;
  }

  selectState(state: EmployeeState): void {
    this.selectedStateId = state.stateId;
    this.stateSearch = state.stateName;
    this.stateDropdownOpen = false;
    this.onStateChange();
  }

  selectCity(city: EmployeeCity): void {
    this.selectedCityId = city.cityId;
    this.citySearch = city.cityName;
    this.cityDropdownOpen = false;
    this.onCityChange();
  }

  selectCampus(campus: EmployeeCampus): void {
    this.selectedCampusId = campus.campusId;
    this.campusSearch = campus.campusName;
    this.campusDropdownOpen = false;
  }

  openStateDropdown(): void {
    if (!this.stateDropdownOpen) {
      this.stateDropdownOpen = true;
      this.stateSearchEdited = false;
      this.cityDropdownOpen = false;
      this.campusDropdownOpen = false;
    }
  }

  openCityDropdown(): void {
    if (!this.selectedStateId) {
      return;
    }
    this.cityDropdownOpen = true;
    this.citySearchEdited = false;
    this.stateDropdownOpen = false;
    this.campusDropdownOpen = false;
  }

  openCampusDropdown(): void {
    if (!this.selectedCityId) {
      return;
    }
    this.campusDropdownOpen = true;
    this.campusSearchEdited = false;
    this.stateDropdownOpen = false;
    this.cityDropdownOpen = false;
  }

  onStateChange(): void {
    this.selectedCityId = null;
    this.selectedCampusId = null;
    this.citySearch = '';
    this.campusSearch = '';
    this.cities = [];
    this.campuses = [];
    if (!this.selectedStateId) {
      return;
    }
    this.wellbeingService.getEmployeeCities(this.selectedStateId).subscribe({
      next: (list) => {
        this.cities = Array.isArray(list) ? list : [];
      },
      error: () => {
        this.cities = [];
      },
    });
  }

  onCityChange(): void {
    this.selectedCampusId = null;
    this.campusSearch = '';
    this.campuses = [];
    if (!this.selectedCityId) {
      return;
    }
    this.wellbeingService.getEmployeeCampuses(this.selectedCityId).subscribe({
      next: (list) => {
        this.campuses = Array.isArray(list) ? list : [];
      },
      error: () => {
        this.campuses = [];
      },
    });
  }

  resetFilters(): void {
    this.selectedStateId = null;
    this.selectedCityId = null;
    this.selectedCampusId = null;
    this.fromDate = null;
    this.toDate = null;
    this.stateSearch = '';
    this.citySearch = '';
    this.campusSearch = '';
    this.stateSearchEdited = false;
    this.citySearchEdited = false;
    this.campusSearchEdited = false;
    this.closeSelectDropdowns();
    this.cities = [];
    this.campuses = [];
  }

  private closeSelectDropdowns(): void {
    this.stateDropdownOpen = false;
    this.cityDropdownOpen = false;
    this.campusDropdownOpen = false;
  }

  private filterListBySearch<T>(list: T[], nameKey: keyof T, search: string): T[] {
    const query = String(search ?? '').trim().toLowerCase();
    if (!query) {
      return list;
    }
    return list.filter((item) => String(item[nameKey] ?? '').toLowerCase().includes(query));
  }

  applyFilters(): void {
    this.filterOpen = false;
    this.serverPaginationEnabled = true;
    this.currentPage = 0;
    this.spinner.show();
    this.gridApi?.showLoadingOverlay();

    if (!this.gridApi) {
      return;
    }

    this.refreshReportGrid();
  }


  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    this.columnDefs = this.buildBaseColumnDefs();
    const api = this.gridApi as GridApi & {
      setGridOption(key: string, value: unknown): void;
    };
    api.setGridOption('serverSideDatasource', this.createReportDatasource());
    this.loadRecentReport();
  }

  exportExcel(): void {
    if (!this.gridApi || (!this.rowData.length && !this.totalRecords)) {
      return;
    }
    const isConfirmed = confirm(
      'Are you sure you want to download the Excel report?',
    );

    if (!isConfirmed) {
      return;
    }

    if (this.serverPaginationEnabled && this.totalRecords > 0) {
      this.spinner.show();
      const from = this.formatDateForApi(this.fromDate);
      const to = this.formatDateForApi(this.toDate);
      this.wellbeingService
        .getStudentWellBeingReport(
          this.selectedStateId,
          this.categoryName,
          this.selectedCityId,
          this.selectedCampusId,
          from || undefined,
          to || undefined,
          0,
          this.totalRecords,
        )
        .subscribe({
          next: (result) => {
            void this.buildAndDownloadExcel(result.rows);
            this.spinner.hide();
          },
          error: () => {
            this.spinner.hide();
          },
        });
      return;
    }

    void this.buildAndDownloadExcel();
  }

  private loadReportLogoDataUrl(): Promise<string | null> {
    return this.loadReportLogoBase64().then((base64) =>
      base64 ? `data:image/png;base64,${base64}` : null,
    );
  }

  private loadReportLogoBase64(): Promise<string | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          resolve(dataUrl.split('base64,')[1] ?? null);
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = 'assets/image/favicon.ico';
    });
  }

  private async buildAndDownloadExcel(exportReportRows?: StudentWellBeingReportRow[]): Promise<void> {
    const savedQuestionIds = [...this.questionIds];
    const savedColumnDefs = [...this.columnDefs];
    if (exportReportRows?.length) {
      this.setupColumnDefsFromReportRows(exportReportRows);
    }

    try {
    const exportColumns = this.getExcelExportColumns();
    const headers = exportColumns.map((c) => c.headerName);
    const rowsForExport = exportReportRows
      ? this.mapReportRowsToGridData(exportReportRows)
      : this.rowData;
    const reportRowsForLookup = exportReportRows ?? this.reportRows;
    const data = rowsForExport.map((row) =>
      exportColumns.map((c) => {
        const field = c.field;
        if (field.startsWith('q_')) {
          const questionId = Number(field.replace('q_', ''));
          const admNo = String(row['admNo'] ?? '').trim();
          const feedBackDate = String(row['feedBackDate'] ?? '').trim();
          return this.getExcelQuestionCellValue(admNo, feedBackDate, questionId, reportRowsForLookup);
        }
        const value = row[field];
        return value == null ? '' : String(value);
      }),
    );

    const institute = 'Sri Chaitanya Educational Institutions';
    const reportTitle = 'Student well Being Report';
    const totalCols = Math.max(headers.length, 3);
    const startCol = 3;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Reports');

    const logoBase64 = await this.loadReportLogoBase64();
    if (logoBase64) {
      const logo = workbook.addImage({
        base64: logoBase64,
        extension: 'png',
      });
      worksheet.addImage(logo, {
        tl: { col: 0, row: 0 },
        ext: { width: 72, height: 72 },
      });
      worksheet.getRow(1).height = 54;
      worksheet.getRow(2).height = 22;
    }

    worksheet.mergeCells(1, startCol, 1, totalCols);
    worksheet.getCell(1, startCol).value = institute;
    worksheet.getCell(1, startCol).font = { name: 'Times New Roman', size: 16, bold: true };
    worksheet.getCell(1, startCol).alignment = { vertical: 'middle' };

    worksheet.mergeCells(2, startCol, 2, totalCols);
    worksheet.getCell(2, startCol).value = reportTitle;
    worksheet.getCell(2, startCol).font = { name: 'Times New Roman', size: 14, bold: true };
    worksheet.getCell(2, startCol).alignment = { vertical: 'middle' };

    worksheet.addRow([]);
    worksheet.addRow([]);

    const headerRow = worksheet.addRow(headers);
    headerRow.height = 22;
    headerRow.eachCell((cell: Cell) => {
      cell.font = { name: 'Times New Roman', size: 10, bold: true };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    data.forEach((rowData) => {
      const row = worksheet.addRow(rowData);
      row.eachCell((cell: Cell) => {
        cell.font = { name: 'Times New Roman', size: 10 };
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        cell.alignment = { vertical: 'middle' };
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    fs.saveAs(blob, 'StudentWellBeingReport.xlsx');
    } finally {
      if (exportReportRows?.length) {
        this.questionIds = savedQuestionIds;
        this.columnDefs = savedColumnDefs;
      }
    }
  }

  exportPdf(rowContext?: { admNo: string; feedBackDate: string }): void {
    if (!this.rowData.length) {
      return;
    }
    const confirmMessage = rowContext
      ? `Do you want to download the PDF report for Adm No ${rowContext.admNo}?`
      : 'Do you want to download the PDF report?';
    const isConfirmed = confirm(confirmMessage);

    if (!isConfirmed) {
      return;
    }

    const studentContext = rowContext ? this.buildPdfStudentContext(rowContext) : null;
    this.spinner.show();
    void this.loadReportLogoDataUrl().then((logoDataUrl) => {
      this.wellbeingService.getFeedBackQuestionAndOptions(this.categoryName).subscribe({
        next: (questions) => {
          this.generatePdf(questions, studentContext, logoDataUrl);
          this.spinner.hide();
        },
        error: () => {
          this.generatePdf([], studentContext, logoDataUrl);
          this.spinner.hide();
        },
      });
    });
  }

  private isTextOption(opt: FeedbackHistoryOption | { optionName?: string; option?: string }): boolean {
    const label = String(
      ('optionName' in opt ? opt.optionName : opt.option) ?? '',
    ).trim();
    return label.toUpperCase() === 'TEXT';
  }

  private getPdfOptionLabel(
    opt: { optionId: number; optionRank: number; optionName?: string },
    studentContext: PdfStudentContext | null | undefined,
    questionId: number,
  ): string {
    const optionName = String(opt.optionName ?? '').trim();
    if (!this.isTextOption({ optionName })) {
      return optionName;
    }
    if (!studentContext) {
      return optionName;
    }
    const selections = studentContext.selectionsByQuestionId.get(questionId) ?? [];
    const match = selections.find(
      (selected) =>
        selected.optionId === Number(opt.optionId) &&
        selected.optionRank === Number(opt.optionRank),
    );
    if (match?.feedbackOptionText) {
      return match.feedbackOptionText;
    }
    const textAnswer = selections.find((selected) => selected.feedbackOptionText)?.feedbackOptionText;
    return textAnswer || optionName;
  }

  private getOptionDisplayText(opt: FeedbackHistoryOption): string {
    const text = String(opt.feedbackOptionText ?? '').trim();
    if (text) {
      return text;
    }
    const option = String(opt.option ?? '').trim();
    if (option && !this.isTextOption(opt)) {
      return option;
    }
    return '';
  }

  private getExcelQuestionCellValue(
    admNo: string,
    feedBackDate: string,
    questionId: number,
    reportRowsSource?: StudentWellBeingReportRow[],
  ): string {
    const reportRow = this.findReportRow(admNo, feedBackDate, reportRowsSource);
    if (!reportRow) {
      return '';
    }
    const question = (reportRow.questionAndOption ?? []).find((q) => q.questionId === questionId);
    const selected = (question?.options ?? []).filter((o) => o.status);
    if (!selected.length) {
      return '';
    }
    const parts = selected
      .map((opt) => {
        if (this.isTextOption(opt)) {
          return this.getOptionDisplayText(opt);
        }
        const rank = this.normalizeOptionRank(opt.optionRank);
        return rank >= 1 && rank <= 5 ? String(rank) : '';
      })
      .filter(Boolean);
    return parts.join(', ');
  }

  // private generatePdf(questions: FeedBackQuestionAndOptionApi[]): void {
  //   const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  //   const pageWidth = doc.internal.pageSize.getWidth();
  //   const margin = 14;
  //   const contentWidth = pageWidth - margin * 2;
  //   let y = 18;

  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(16);
  //   doc.text('Student Well Being Report', margin, y);
  //   y += 8;

  //   doc.setFont('helvetica', 'normal');
  //   doc.setFontSize(10);

  //   const sortedQuestions = [...questions].sort((a, b) => a.questionId - b.questionId);
  //   if (sortedQuestions.length) {
  //     y = this.ensurePdfPageSpace(doc, y, 12);
  //     doc.setFont('helvetica', 'bold');
  //     doc.setFontSize(12);
  //     // doc.text('Questions & Options', margin, y);
  //     y += 7;

  //     const questionFontSize = 11;
  //     const optionFontSize = 9;
  //     sortedQuestions.forEach((question, index) => {
  //       const questionText = String(question.questionTitle ?? question.question ?? '').trim();
  //       doc.setFont('helvetica', 'bold');
  //       doc.setFontSize(questionFontSize);
  //       const wrappedQuestion = doc.splitTextToSize(`Q${index + 1}. ${questionText}`, contentWidth);
  //       const questionLineHeight = questionFontSize * 0.45;
  //       const questionHeight = wrappedQuestion.length * questionLineHeight;
  //       y = this.ensurePdfPageSpace(doc, y, questionHeight + 4);
  //       doc.text(wrappedQuestion, margin, y);
  //       y += questionHeight;

  //       doc.setFont('helvetica', 'normal');
  //       doc.setFontSize(optionFontSize);
  //       const options = [...(question.options ?? [])].sort((a, b) => a.optionRank - b.optionRank);
  //       options.forEach((opt) => {
  //         const optionLine = doc.splitTextToSize(
  //           `   • ${String(opt.optionName ?? '').trim()}`,
  //           contentWidth,
  //         );
  //         const optionLineHeight = optionFontSize * 0.45;
  //         const optionHeight = optionLine.length * optionLineHeight;
  //         y = this.ensurePdfPageSpace(doc, y, optionHeight);
  //         doc.text(optionLine, margin, y);
  //         y += optionHeight;
  //       });
  //       y += 3;
  //     });
  //     y += 4;
  //   }

  //   doc.save('StudentWellBeingReport.pdf');
  // }

  private generatePdf(
    questions: FeedBackQuestionAndOptionApi[],
    studentContext?: PdfStudentContext | null,
    logoDataUrl?: string | null,
  ): void {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2;
    let y = this.drawPdfReportHeader(doc, margin, logoDataUrl ?? null);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (studentContext) {
      const rightX = pageWidth - margin;
      const infoY = y;
      doc.text(`Adm No: ${studentContext.admNo}`, margin, infoY);
      if (studentContext.studentName) {
        doc.text(`Student Name: ${studentContext.studentName}`, rightX, infoY, { align: 'right' });
      }
      y += 5;
      doc.text(`FeedBack Date: ${studentContext.feedBackDate}`, margin, y);
      if (studentContext.sectionName) {
        doc.text(`Section Name: ${studentContext.sectionName}`, rightX, y, { align: 'right' });
      }
      y += 7;
    }

    const sortedQuestions = [...questions].sort((a, b) => a.questionId - b.questionId);
    if (sortedQuestions.length) {
      y = this.ensurePdfPageSpace(doc, y, 12);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      // doc.text('Questions & Options', margin, y);
      y += 7;

      const questionFontSize = 11;
      const optionFontSize = 9;
      sortedQuestions.forEach((question, index) => {
        const questionText = String(question.questionTitle ?? question.question ?? '').trim();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(questionFontSize);
        const wrappedQuestion = doc.splitTextToSize(`Q${index + 1}. ${questionText}`, contentWidth);
        const questionLineHeight = questionFontSize * 0.45;
        const questionHeight = wrappedQuestion.length * questionLineHeight;
        y = this.ensurePdfPageSpace(doc, y, questionHeight + 4);
        doc.text(wrappedQuestion, margin, y);
        y += questionHeight;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(optionFontSize);
        const options = [...(question.options ?? [])].sort((a, b) => a.optionRank - b.optionRank);
        options.forEach((opt, optIndex) => {
          const optionLabel = this.getPdfOptionLabel(opt, studentContext, question.questionId);
          const isSelected = this.isPdfOptionSelected(
            studentContext,
            question.questionId,
            opt.optionId,
            opt.optionRank,
          );
          const optionText = `   ${optIndex + 1}. ${optionLabel}`;
          const optionLine = doc.splitTextToSize(optionText, contentWidth);
          const optionLineHeight = optionFontSize * 0.45;
          const optionHeight = optionLine.length * optionLineHeight;
          y = this.ensurePdfPageSpace(doc, y, optionHeight);
          const lastLineIndex = optionLine.length - 1;
          optionLine.forEach((line: string, lineIndex: number) => {
            const lineY = y + lineIndex * optionLineHeight;
            doc.text(line, margin, lineY);
            if (isSelected && lineIndex === lastLineIndex) {
              const tickX = margin + doc.getTextWidth(line) + 1.5;
              this.drawPdfCheckmark(doc, tickX, lineY);
            }
          });
          y += optionHeight;
        });
        y += 3;
      });
      y += 4;
    }

    const fileName = studentContext
      ? `StudentWellBeingReport_${studentContext.admNo}.pdf`
      : 'StudentWellBeingReport.pdf';
    doc.save(fileName);
  }

  private buildPdfStudentContext(rowContext: { admNo: string; feedBackDate: string }): PdfStudentContext | null {
    const reportRow = this.findReportRow(rowContext.admNo, rowContext.feedBackDate);
    if (!reportRow) {
      return null;
    }
    const selectionsByQuestionId = new Map<
      number,
      { optionId: number; optionRank: number; feedbackOptionText: string }[]
    >();
    (reportRow.questionAndOption ?? []).forEach((question) => {
      const selected = (question.options ?? [])
        .filter((opt) => opt.status)
        .map((opt) => ({
          optionId: Number(opt.optionId),
          optionRank: this.normalizeOptionRank(opt.optionRank),
          feedbackOptionText: String(opt.feedbackOptionText ?? '').trim(),
        }));
      if (selected.length) {
        selectionsByQuestionId.set(question.questionId, selected);
      }
    });
    return {
      admNo: String(reportRow.admNo ?? '').trim(),
      feedBackDate: String(reportRow.feedBackDate ?? '').trim(),
      studentName: String(reportRow.studentName ?? '').trim() || undefined,
      sectionName: String(reportRow.sectionName ?? '').trim() || undefined,
      selectionsByQuestionId,
    };
  }

  private findReportRow(
    admNo: string,
    feedBackDate: string,
    source?: StudentWellBeingReportRow[],
  ): StudentWellBeingReportRow | undefined {
    const rows = source ?? this.reportRows;
    return rows.find(
      (entry) =>
        String(entry.admNo ?? '').trim() === admNo &&
        String(entry.feedBackDate ?? '').trim() === feedBackDate,
    );
  }

  private drawPdfReportHeader(doc: jsPDF, margin: number, logoDataUrl: string | null): number {
    const logoSize = 16;
    const headerTop = 12;
    const textX = margin + logoSize + 5;

    if (logoDataUrl) {
      try {
        doc.addImage(logoDataUrl, 'PNG', margin, headerTop, logoSize, logoSize);
      } catch {
        // Skip logo if it cannot be rendered in the PDF.
      }
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Sri Chaitanya Educational Institutions', textX, headerTop + 6);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Student well Being Report', textX, headerTop + 13);

    return headerTop + logoSize + 6;
  }

  private drawPdfCheckmark(doc: jsPDF, x: number, baselineY: number): void {
    const size = 2.8;
    doc.setDrawColor(22, 163, 74);
    doc.setLineWidth(0.4);
    doc.line(x, baselineY - size * 0.4, x + size * 0.38, baselineY);
    doc.line(x + size * 0.38, baselineY, x + size * 1.05, baselineY - size * 0.9);
  }

  private isPdfOptionSelected(
    studentContext: PdfStudentContext | null | undefined,
    questionId: number,
    optionId: number,
    optionRank: number,
  ): boolean {
    if (!studentContext) {
      return false;
    }
    const selections = studentContext.selectionsByQuestionId.get(questionId) ?? [];
    return selections.some(
      (selected) => selected.optionId === Number(optionId) && selected.optionRank === Number(optionRank),
    );
  }

  private ensurePdfPageSpace(doc: jsPDF, y: number, needed: number): number {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + needed > pageHeight - 14) {
      doc.addPage();
      return 18;
    }
    return y;
  }

  private getSelectedCampusName(): string {
    const campus = this.campuses.find((c) => c.campusId === this.selectedCampusId);
    return campus?.campusName ?? '';
  }

  private openQuestionPaperDialog(rowContext: {
    admNo: string;
    feedBackDate: string;
    studentName?: string;
  }): void {
    const reportRow = this.findReportRow(rowContext.admNo, rowContext.feedBackDate);
    if (!reportRow) {
      return;
    }

    const dialogData: ShowQuestionPaperDialogData = {
      categoryName: this.categoryName,
      admNo: rowContext.admNo,
      feedBackDate: rowContext.feedBackDate,
      studentName: String(reportRow.studentName ?? rowContext.studentName ?? '').trim() || undefined,
      sectionName: String(reportRow.sectionName ?? '').trim() || undefined,
      questionAndOption: reportRow.questionAndOption ?? [],
      onDownloadPdf: (ctx) => this.exportPdf(ctx),
    };

    this.dialog.open(ShowQuestionPaperComponent, {
      width: '42%',
      minWidth: '360px',
      height: '100%',
      data: dialogData,
      panelClass: 'show-question-paper-right-dialog',
      position: { right: '0px' },
      animation: { to: 'left' },
      maxWidth: '100%',
      autoFocus: false,
      restoreFocus: true,
    });
  }

  onCellClicked(event: CellClickedEvent): void {
    const field = String(event.colDef?.field ?? '');
    const row = event.data as Record<string, unknown>;
    const admNo = String(row['admNo'] ?? '').trim();
    const feedBackDate = String(row['feedBackDate'] ?? '').trim();
    const studentName = String(row['studentName'] ?? '').trim();

    if (field === 'feedBackDate') {
      return;
    }

    if (field === 'admNo') {
      if (admNo && feedBackDate) {
        this.openQuestionPaperDialog({ admNo, feedBackDate, studentName });
      }
      return;
    }

    if (!field.startsWith('q_') || event.value == null) {
      return;
    }

    const questionId = Number(field.replace('q_', ''));
    if (!Number.isFinite(questionId)) {
      return;
    }

    const reportRow = this.findReportRow(admNo, feedBackDate);
    if (!reportRow) {
      return;
    }

    const question = (reportRow.questionAndOption ?? []).find((q) => q.questionId === questionId);
    if (!question) {
      return;
    }

    const selected = (question.options ?? []).filter((o) => o.status);
    if (!selected.length) {
      return;
    }

    const qIndex = this.questionIds.indexOf(questionId);
    const dialogData: SingleQuestionDialogData = {
      feedBackDate,
      admNo,
      studentName,
      questionId,
      question: String(question.question ?? '').trim(),
      questionLabel: qIndex >= 0 ? `Q${qIndex + 1}` : 'Q',
      options: selected.map((opt) => ({
        optionRank: this.normalizeOptionRank(opt.optionRank),
        feedbackOptionText: this.getOptionDisplayText(opt),
      })),
    };

    this.dialog.open(SingleQuestionDialogComponent, {
      width: '32%',
      minWidth: '320px',
      height: '100%',
      data: dialogData,
      panelClass: 'single-question-right-dialog',
      position: { right: '0px' },
      animation: { to: 'left' },
      maxWidth: '100%',
      autoFocus: false,
      restoreFocus: true,
    });
  }

  private normalizeReportResponse(data: unknown): StudentWellBeingReportRow[] {
    if (Array.isArray(data)) {
      return data;
    }
    if (data && typeof data === 'object') {
      return [data as StudentWellBeingReportRow];
    }
    return [];
  }

  private buildBaseColumnDefs(): ColDef[] {
    const linkCell: ColDef = {
      cellClass: 'report-pdf-link-cell',
      cellStyle: { textAlign: 'center', fontWeight: '600', cursor: 'pointer' },
    };
    return [
      {
        field: 'admNo',
        headerName: 'Adm No',
        pinned: 'left',
        minWidth: 120,
        maxWidth: 120,
        ...linkCell,
      },
      {
        field: 'feedBackDate',
        headerName: 'FeedBack Date',
        pinned: 'left',
        minWidth: 140,
        maxWidth: 140,
      },
      { 
        field:'campusName',
        headerName: 'Campus Name',
        pinned: 'left', 
      }
    ];
  }

  private getExcelExportColumns(): { field: string; headerName: string }[] {
    const excelOnlyFields = new Set(['studentName', 'sectionName', 'campusName']);
    const gridColumns = this.columnDefs
      // .filter((c) => c.field)
      .filter((c) => c.field && !excelOnlyFields.has(String(c.field)))
      .map((c) => ({
        field: String(c.field),
        headerName: String(c.headerName ?? c.field ?? ''),
      }));
    const extraColumns = [
      { field: 'studentName', headerName: 'Student Name' },
      { field: 'sectionName', headerName: 'Section Name' },
      { field: 'campusName', headerName: 'Campus Name' },
    ];
    const feedBackIdx = gridColumns.findIndex((c) => c.field === 'feedBackDate');
    if (feedBackIdx === -1) {
      return [...gridColumns, ...extraColumns];
    }
    return [
      ...gridColumns.slice(0, feedBackIdx + 1),
      ...extraColumns,
      ...gridColumns.slice(feedBackIdx + 1),
    ];
  }

  private buildGrid(reportRows: StudentWellBeingReportRow[]): void {
    this.reportRows = reportRows;
    this.setupColumnDefsFromReportRows(reportRows);
    this.rowData = this.mapReportRowsToGridData(reportRows);
    this.gridApi?.setGridOption('columnDefs', this.columnDefs);
  }

  private refreshReportGrid(): void {
    if (!this.gridApi) {
      return;
    }
    const api = this.gridApi as GridApi & {
      setGridOption(key: string, value: unknown): void;
      refreshServerSide(options: { purge: boolean }): void;
    };
    api.setGridOption('pagination', this.serverPaginationEnabled);
    api.setGridOption('paginationPageSize', this.pageSize);
    api.setGridOption('cacheBlockSize', this.pageSize);
    api.refreshServerSide({ purge: true });
  }

  private setupColumnDefsFromReportRows(reportRows: StudentWellBeingReportRow[]): void {
    this.questionIds = [];
    const questionIdSet = new Set<number>();

    reportRows.forEach((row) => {
      (row.questionAndOption ?? []).forEach((q) => {
        if (!questionIdSet.has(q.questionId)) {
          questionIdSet.add(q.questionId);
          this.questionIds.push(q.questionId);
        }
      });
    });

    this.questionIds.sort((a, b) => a - b);

    this.columnDefs = [
      ...this.buildBaseColumnDefs(),
      ...this.questionIds.map((questionId, index) => {
        const qNum = index + 1;
        const isWideQuestion = qNum === 4 || qNum === 5;
        return {
          field: `q_${questionId}`,
          headerName: `Q${qNum}`,
          minWidth: isWideQuestion ? 160 : 88,
          maxWidth: isWideQuestion ? 300 : undefined,
          cellRenderer: (params: { value: unknown }) => this.renderRankCell(params.value),
          cellStyle: (params: { value: unknown }) => this.getQuestionCellStyle(params.value),
        };
      }),
    ];
  }

  private mapReportRowsToGridData(reportRows: StudentWellBeingReportRow[]): Record<string, unknown>[] {
    return reportRows.map((entry) => {
      const row: Record<string, unknown> = {
        admNo: entry.admNo ?? '',
        feedBackDate: entry.feedBackDate ?? '',
        studentName: entry.studentName ?? '',
        sectionName: entry.sectionName ?? '',
        campusName: entry.campusName ?? '',
      };

      this.questionIds.forEach((questionId) => {
        const question = (entry.questionAndOption ?? []).find((q) => q.questionId === questionId);
        const selected = (question?.options ?? []).filter((o) => o.status);
        const ranks = selected
          .map((o) => o.optionRank)
          .filter((r): r is number => r != null && r >= 1 && r <= 5);

        row[`q_${questionId}`] = ranks.length ? ranks : null;
      });

      return row;
    });
  }

  private createReportDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        if (!this.serverPaginationEnabled) {
          this.wellbeingService.getRecentFeedBackReport(this.categoryName).subscribe({
            next: (data) => {
              const list = this.normalizeReportResponse(data);
              this.buildGrid(list);
              const gridRows = this.rowData;
              this.totalRecords = list.length;
              this.totalPages = list.length > 0 ? 1 : 0;
              this.currentPage = 0;

              params.success({
                rowData: gridRows,
                rowCount: list.length,
              });

              this.spinner.hide();
              if (!list.length) {
                this.gridApi?.showNoRowsOverlay();
              } else {
                this.gridApi?.hideOverlay();
              }
            },
            error: () => {
              this.rowData = [];
              this.reportRows = [];
              this.columnDefs = this.buildBaseColumnDefs();
              this.gridApi?.setGridOption('columnDefs', this.columnDefs);
              params.fail();
              this.spinner.hide();
              this.gridApi?.showNoRowsOverlay();
            },
          });
          return;
        }

        const startRow = params.request.startRow ?? 0;
        const page = Math.floor(startRow / this.pageSize);
        const from = this.formatDateForApi(this.fromDate);
        const to = this.formatDateForApi(this.toDate);

        this.gridApi?.showLoadingOverlay();

        this.wellbeingService
          .getStudentWellBeingReport(
            this.selectedStateId,
            this.categoryName,
            this.selectedCityId,
            this.selectedCampusId,
            from || undefined,
            to || undefined,
            page,
            this.pageSize,
          )
          .subscribe({
            next: (result) => {
              this.totalRecords = result.totalRecords;
              this.totalPages = result.totalPages;
              this.currentPage = result.currentPage;
              this.reportRows = result.rows;

              if (page === 0) {
                this.setupColumnDefsFromReportRows(result.rows);
                this.gridApi?.setGridOption('columnDefs', this.columnDefs);
              }

              const gridRows = this.mapReportRowsToGridData(result.rows);
              this.rowData = gridRows;

              params.success({
                rowData: gridRows,
                rowCount: result.totalRecords,
              });

              this.spinner.hide();
              if (!result.rows.length) {
                this.gridApi?.showNoRowsOverlay();
              } else {
                this.gridApi?.hideOverlay();
              }
            },
            error: () => {
              this.rowData = [];
              this.reportRows = [];
              this.totalRecords = 0;
              this.totalPages = 0;
              params.fail();
              this.spinner.hide();
              this.gridApi?.showNoRowsOverlay();
            },
          });
      },
    };
  }

  private renderRankCell(value: unknown): string {
    const ranks = this.parseRanks(value);
    if (!ranks.length) {
      return '';
    }
    return ranks
      .map((rank) => {
        const palette = RANK_PALETTE[rank] ?? RANK_PALETTE[1];
        return `<span class="report-rank-badge" style="background-color:${palette.circle};border:1px solid ${palette.circleBorder}">${rank}</span>`;
      })
      .join('');
  }

  private getRankCellStyle(value: unknown): Record<string, string> | undefined {
    const ranks = this.parseRanks(value);
    if (!ranks.length) {
      return undefined;
    }
    const palette = RANK_PALETTE[ranks[0]] ?? RANK_PALETTE[1];
    return {
      border: `1.5px solid ${palette.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
  }

  private getQuestionCellStyle(value: unknown): Record<string, string> {
    const rankStyle = this.getRankCellStyle(value);
    const base: Record<string, string> = {
      textAlign: 'center',
      cursor: value != null ? 'pointer' : 'default',
    };
    return rankStyle ? { ...base, ...rankStyle } : base;
  }

  private normalizeOptionRank(rank: unknown): number {
    const value = Number(rank);
    return value >= 1 && value <= 5 ? value : 1;
  }

  private parseRanks(value: unknown): number[] {
    if (value == null) {
      return [];
    }
    if (Array.isArray(value)) {
      return value.filter((r): r is number => typeof r === 'number' && r >= 1 && r <= 5);
    }
    if (typeof value === 'number') {
      return value >= 1 && value <= 5 ? [value] : [];
    }
    return [];
  }

  private coerceToDate(value: unknown): Date | null {
    if (value == null) {
      return null;
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }
    const momentLike = value as { toDate?: () => Date };
    if (typeof momentLike.toDate === 'function') {
      const d = momentLike.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  private formatDateForApi(date: unknown): string {
    const parsed = this.coerceToDate(date);
    if (!parsed) {
      return '';
    }
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
